import {
  WIZARD_STEPS,
  type IdealDesignDto,
  type OnboardingState,
  type SamePairDto,
  type SetupProgressDto,
  type StepVerdict,
  type WizardStepKey,
} from '@bk/contracts';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, Card, ErrorText } from '../../../ui';
import { useSetupProgress } from '../Setup';
import { useTalk } from './Talk';

/** I passi che si giudicano con i tre tasti (ADR 0061): tutti quelli dopo la visura. */
export const isJudgedStep = (step: WizardStepKey) =>
  WIZARD_STEPS.indexOf(step) > WIZARD_STEPS.indexOf('visura');

/**
 * I tre tasti in fondo al passo (ADR 0061): «Giusto» conferma ciò che aspetta la persona, «Può andar bene» accetta il
 * passo e lascia da verificare ciò che è supposto, «Correggiamo» scrive nella chat del passo, che lo aggiorna da sola.
 */
export function StepVerdictBar({ step }: { step: WizardStepKey }) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const progress = useSetupProgress();
  const esito = progress.data?.guidata.esiti?.[step] ?? null;
  // «Correggiamo» apre «Parliamone» (ADR 0063): un solo posto per correggere, dove si vede cosa ha cambiato il cervello.
  const talk = useTalk();
  const judge = useMutation({
    mutationFn: (valore: StepVerdict | null) =>
      api<SetupProgressDto['guidata']>(t('/onboarding/wizard'), {
        body: { esito: { passo: step, valore } },
      }),
    onSuccess: (guidata) => {
      qc.setQueryData<SetupProgressDto>(['tenant', tenant.id, 'onboarding-progress'], (old) =>
        old ? { ...old, guidata } : old,
      );
      // «Giusto» conferma le voci del passo: le pagine si rileggono in background, senza avvisi.
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  if (!isManager) return null;
  const busy = judge.isPending;
  // Toccare di nuovo il tasto scelto toglie il giudizio.
  const pick = (v: StepVerdict) => judge.mutate(esito === v ? null : v);
  return (
    <Card className="space-y-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-sm font-medium">Com’è questo passo?</span>
        <Button
          variant={esito === 'giusto' ? 'primary' : 'ghost'}
          aria-pressed={esito === 'giusto'}
          className={esito === 'giusto' ? '' : 'border border-black/10'}
          disabled={busy}
          onClick={() => pick('giusto')}
        >
          Giusto
        </Button>
        <Button
          variant={esito === 'va_bene' ? 'primary' : 'ghost'}
          aria-pressed={esito === 'va_bene'}
          className={esito === 'va_bene' ? '' : 'border border-black/10'}
          disabled={busy}
          onClick={() => pick('va_bene')}
        >
          Può andar bene
        </Button>
        <Button
          variant="ghost"
          className="border border-black/10"
          disabled={busy}
          onClick={() => talk.open()}
        >
          Correggiamo
        </Button>
      </div>
      {judge.error && <ErrorText>{errorMessage(judge.error)}</ErrorText>}
      <p className="text-xs text-ink/45">
        {esito === 'va_bene'
          ? 'Ciò che è supposto resta da verificare: lo ritrovi più avanti.'
          : 'Giusto conferma tutto il passo; Può andar bene lo accetta e lascia da verificare ciò che è supposto; Correggiamo apre «Parliamone».'}
      </p>
    </Card>
  );
}

/** L'apertura di un passo in corso verso il server (ADR 0069): la manda il wizard, la guardano le domande. */
export const stepOpenKey = (tenantId: string, step: WizardStepKey) => ['step-open', tenantId, step];

/** I lavori del server che riscrivono le domande di un passo (le etichette di `preparingSteps`). */
const REWRITES_QUESTIONS = [
  'Compilo sedi e attività dalla visura e dal web',
  'Progetto come dovrebbe essere questa parte dell’azienda',
  'Progetto impianti e attrezzature delle sedi',
];

const STEP_NAME: Record<WizardStepKey, string> = {
  visura: 'Visura',
  impresa: 'L’impresa',
  sedi: 'Sedi',
  attivita: 'Attività economiche',
  impianti: 'Impianti e attrezzature',
  software: 'Programmi e servizi online',
  organizzazione: 'Organizzazione e ruoli',
  procedure: 'Procedure',
  processi: 'I processi',
  obblighi: 'Gli obblighi',
  diagnosi: 'La diagnosi',
  proposte: 'Proposte e percorso',
};

/**
 * Ciò che il cervello con strumenti dice al titolare (ADR 0055, 0061, 0062): le domande con la risposta probabile in
 * evidenza, ciò che non torna nei passi prima e cosa ha cambiato. Uguale in ogni passo che le ha.
 */
export function BrainNotes({
  step,
  d,
  what,
  rethinking,
}: {
  step: WizardStepKey;
  /** Il progetto del passo; Impianti (ADR 0065) passa solo le domande della sede e se il riferimento è pronto. */
  d: Partial<IdealDesignDto> | null;
  what: string;
  /** Il cervello sta rifacendo le domande; senza, lo si ricava dai lavori del passo. Impianti lo dice sede per sede. */
  rethinking?: boolean;
}) {
  const { t, tenant, isManager } = useTenant();
  // Mentre il passo si apre o il cervello lo rifà in background, le domande di prima non si mostrano (ADR 0066) ma non
  // spariscono in silenzio: al loro posto una riga dice che le sto rivedendo, e le nuove compaiono da sé.
  const progress = useSetupProgress();
  const opening = useIsMutating({ mutationKey: stepOpenKey(tenant.id, step) }) > 0;
  const lavori = progress.data?.wizard.find((w) => w.chiave === step)?.lavori ?? [];
  const busy = rethinking ?? lavori.some((l) => REWRITES_QUESTIONS.includes(l));
  const [answered, setAnswered] = useState<Record<string, string>>({});
  const qc = useQueryClient();
  // La risposta va in «Parliamone» come messaggio della persona (ADR 0063): lì si vede cosa ha cambiato il cervello.
  const answer = useMutation({
    mutationFn: (v: { domanda: string; risposta: string }) =>
      api<OnboardingState>(t('/onboarding/messages'), {
        body: { message: `${v.domanda} ${v.risposta}`, passo: step },
      }),
    onSuccess: (next) => {
      qc.setQueryData(['tenant', tenant.id, 'onboarding'], next);
      // Negli impianti la risposta rifà il riferimento delle sedi (ADR 0065): lo stato si rilegge in background.
      if (step === 'impianti') void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'equipment'] });
    },
  });
  const same = <SamePairs step={step} />;
  // Il passo appena aperto: un attimo, finché il server dice se le domande si rifanno.
  if (opening) return same;
  if (busy)
    return (
      <>
        {same}
        <p className="flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm text-ink/60 ring-1 ring-black/5" role="status">
          <span aria-hidden className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
          Rivedo le domande per te con le ultime novità: compaiono qui appena sono pronte.
        </p>
      </>
    );
  if (!d) return same;
  // Le scelte del consulente (ADR 0134) non sono domande: le ha già decise lui e il titolare le corregge a parole.
  const asked = (d.domande ?? []).filter((q) => !q.scelta);
  const chosen = (d.domande ?? []).filter((q) => q.scelta);
  return (
    <>
      {same}
      {!!asked.length && (
        <section
          className="grid gap-3 rounded-2xl bg-white p-4 ring-1 ring-black/5 sm:p-5"
          aria-labelledby={`domande-${step}`}
        >
          <div>
            <h3 id={`domande-${step}`} className="text-base font-semibold">
              {asked.length === 1 ? 'Una cosa che solo tu sai' : `${asked.length} cose che solo tu sai`}
            </h3>
            <p className="text-xs text-ink/50">
              {asked.some((q) => q.probabile)
                ? 'In evidenza c’è la risposta che mi sembra più probabile e che uso se non rispondi: tocca solo dove non è così.'
                : `Le tue risposte cambiano ${what}: tocca quella giusta o scrivimela in «Parliamone».`}
            </p>
          </div>
          {asked.map((q) => {
            const mine = answered[q.domanda];
            return (
              <div key={q.domanda} className="grid gap-2 border-t border-black/5 pt-3">
                <p className="flex flex-wrap items-baseline gap-2 text-sm font-medium">
                  {q.legge && (
                    <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800">Riguarda la legge</span>
                  )}
                  {q.domanda}
                </p>
                {q.perche && <p className="text-xs text-ink/50">{q.perche}</p>}
                {/* Sotto ogni risposta, cosa cambia nel progetto (ADR 0080). */}
                <div className="grid gap-2 sm:grid-cols-2">
                  {q.risposte.map((r, k) => {
                    const on = mine === r;
                    const likely = !mine && q.probabile === r;
                    return (
                      <button
                        key={r}
                        aria-pressed={on}
                        disabled={!isManager || !!mine}
                        onClick={() => {
                          setAnswered((a) => ({ ...a, [q.domanda]: r }));
                          answer.mutate({ domanda: q.domanda, risposta: r });
                        }}
                        className={`grid gap-0.5 rounded-xl px-3 py-2 text-left transition ${on ? 'bg-accent/[0.06] ring-2 ring-accent' : likely ? 'bg-white ring-2 ring-accent/50 hover:ring-accent' : 'bg-white ring-1 ring-black/10 enabled:hover:ring-accent/60'} disabled:cursor-default ${mine && !on ? 'opacity-50' : ''}`}
                      >
                        <span className="text-[13px] font-semibold">
                          {on && <span className="mr-1 text-accent">✓</span>}
                          {r}
                        </span>
                        {q.effetti?.[k] && <span className="text-xs text-ink/60">{q.effetti[k]}</span>}
                        {likely && <span className="text-[11px] font-medium text-accent">La più probabile: vale se non rispondi</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {isManager && asked.some((q) => !answered[q.domanda] && q.probabile) && (
            <button
              className="justify-self-start text-sm font-medium text-accent hover:underline disabled:opacity-50"
              disabled={answer.isPending}
              onClick={() => {
                // «Basta, decidi tu» (ADR 0080): per le domande rimaste valgono le risposte probabili, in un messaggio solo.
                const rest = asked.filter((q) => !answered[q.domanda] && q.probabile);
                setAnswered((a) => ({ ...a, ...Object.fromEntries(rest.map((q) => [q.domanda, q.probabile!])) }));
                answer.mutate({ domanda: 'Basta, decidi tu:', risposta: `\n${rest.map((q) => `${q.domanda} ${q.probabile}`).join('\n')}` });
              }}
            >
              Basta, decidi tu: valgono le risposte più probabili
            </button>
          )}
        </section>
      )}
      {!!chosen.length && (
        <section className="rounded-2xl bg-white px-4 py-3 text-sm ring-1 ring-black/5" aria-label="Le scelte che ho fatto io">
          <p className="font-medium">Le scelte che ho fatto io</p>
          <p className="text-xs text-ink/50">Le ho decise con la risposta più probabile. Se una non è così, scrivimelo in «Parliamone».</p>
          <ul className="mt-2 grid gap-1.5">
            {chosen.map((q) => {
              const k = q.probabile ? q.risposte.indexOf(q.probabile) : -1;
              return (
                <li key={q.domanda} className="border-t border-black/5 pt-1.5">
                  <span className="text-ink/70">{q.domanda}</span> <b className="font-semibold">{q.probabile}</b>
                  {k >= 0 && q.effetti?.[k] && <span className="block text-xs text-ink/50">{q.effetti[k]}</span>}
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {!!d.da_rivedere?.length && (
        <section className="rounded-2xl bg-amber-50 px-4 py-3 text-sm ring-1 ring-amber-200">
          <p className="mb-1 font-medium text-amber-900">Da rivedere nei passi prima</p>
          <ul className="grid gap-1 text-amber-900/80">
            {d.da_rivedere.map((r) => (
              <li key={r.passo + r.cosa}>
                <b className="font-medium">{STEP_NAME[r.passo] ?? r.passo}:</b> {r.cosa}
              </li>
            ))}
          </ul>
        </section>
      )}
      {/* Senza domande la pagina lo dice (ADR 0064): così si distingue «niente da chiedere» da «non ha ancora lavorato». */}
      {asked.length === 0 && !!d.aggiornato && !d.impresa?.generating && (
        <p className="text-xs text-ink/50">
          Nessuna domanda per te in questo passo. Se qualcosa non è come dico, scrivimelo in «Parliamone».
        </p>
      )}
      {d.cambiato && <p className="text-xs text-ink/50">Ultimo aggiornamento: {d.cambiato}</p>}
    </>
  );
}

const PAIR_KIND: Record<SamePairDto['tipo'], string> = { sede: 'la stessa sede', attivita: 'la stessa attività', impianto: 'lo stesso impianto', voce: 'la stessa cosa' };

/**
 * «Sono la stessa cosa?» (ADR 0100): due voci che sembrano la stessa e una è del titolare. Il sistema non le unisce da
 * solo: «Sì» le unisce tenendo la sua, «No» le tiene distinte e non lo chiede più.
 */
export function SamePairs({ step }: { step: WizardStepKey }) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const pairs = useTenantQuery<SamePairDto[]>(['stessi', step], `/onboarding/stessi?passo=${step}`, { refetchInterval: 8000 });
  const answer = useMutation({
    mutationFn: ({ id, stessa }: { id: string; stessa: boolean }) =>
      api<SamePairDto[]>(t(`/onboarding/stessi/${id}`), { body: { stessa } }),
    onSuccess: (next) => {
      qc.setQueryData(['tenant', tenant.id, 'stessi', step], next);
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const list = pairs.data ?? [];
  if (!list.length) return null;
  return (
    <section className="grid gap-3 rounded-2xl bg-white p-4 ring-1 ring-amber-200 sm:p-5" aria-labelledby={`stessi-${step}`}>
      <div>
        <h3 id={`stessi-${step}`} className="text-base font-semibold">
          {list.length === 1 ? 'È la stessa cosa?' : `${list.length} coppie che sembrano la stessa cosa`}
        </h3>
        <p className="text-xs text-ink/50">Una delle due l’hai indicata tu: non le unisco senza il tuo sì.</p>
      </div>
      {list.map((p) => (
        <div key={p.id} className="grid gap-2 border-t border-black/5 pt-3">
          <p className="text-sm">
            <b className="font-medium">«{p.a.nome}»</b> e <b className="font-medium">«{p.b.nome}»</b> sono {PAIR_KIND[p.tipo]}?
          </p>
          {p.perche && <p className="text-xs text-ink/50">{p.perche}</p>}
          {isManager && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                className="ring-1 ring-black/10"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: p.id, stessa: true })}
              >
                Sì, uniscile e tieni «{p.a.nome}»
              </Button>
              <Button
                variant="ghost"
                className="ring-1 ring-black/10"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: p.id, stessa: false })}
              >
                No, sono diverse
              </Button>
            </div>
          )}
        </div>
      ))}
      {answer.error && <ErrorText>{errorMessage(answer.error)}</ErrorText>}
    </section>
  );
}
